import { authService } from "./authService";
import { AUTH_OPTIONAL_ENDPOINTS } from "./apiClient";

// Save the original fetch function
const originalFetch = window.fetch;

// List of public routes that should not trigger login redirect on 401
const publicRoutes = ["/privacy-policy", "/about", "/login", "/register", "/reset-password"];

// Check if current path is a public route
const isPublicRoute = (path: string): boolean => {
  return publicRoutes.some((route) => path.startsWith(route));
};

// Best-effort requests (e.g. app-open telemetry on resume) whose 401s must
// never clear the session — see AUTH_OPTIONAL_ENDPOINTS in apiClient.ts
const isAuthOptionalRequest = (input: RequestInfo | URL): boolean => {
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
      ? input.href
      : input.url;
  return AUTH_OPTIONAL_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

// Create a wrapper for fetch that handles 401 responses
window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
  try {
    // Call the original fetch
    const response = await originalFetch(input, init);

    // If response is 401 Unauthorized, redirect to login
    // But only if we're not on a public route
    if (response.status === 401) {
      const currentPath = window.location.pathname + window.location.search;

      // Don't redirect if we're on a public route or the request is
      // best-effort (its failure must not log the user out)
      if (isPublicRoute(currentPath) || isAuthOptionalRequest(input)) {
        return response;
      }

      authService.handleUnauthorized(currentPath);
    }

    return response;
  } catch (error) {
    // Rethrow the error to be handled by the calling code
    throw error;
  }
};

export const setupFetchInterceptor = () => {
  // This function doesn't need to do anything since the interceptor
  // is set up when this module is imported, but it provides a clean API
  // for initializing the interceptor from App.tsx
};
